<template>
  <Card>
    <template #content>
      <div class="mb-5">
        <h3 class="text-center">Annualized Turns</h3>
      </div>
      <div class="grid grid-cols-12 mb-8">
        <div class="col-span-6 mr-1">
          <RMSAutoComplete
            ref="startWeeksOfYearFilterRef"
            :fieldId="'start-weeks-of-year-autocomplete'"
            :config="{ label: 'Start Week', optionLabel: 'display', showAvailable: false }"
            :loading="startWeeksOfYearLoading"
            :options="startWeeksOfYear"
            :originalValue="startWeekOfYearOriginalValue"
            @onSelected="onSelectStartWeekOfYearFilter"
            @onClear="onClearStartWeekOfYearFilter"
          />
        </div>
        <div class="col-span-6 ml-1 mr-1">
          <RMSAutoComplete
            ref="endWeeksOfYearFilterRef"
            :fieldId="'end-weeks-of-year-autocomplete'"
            :config="{ label: 'End Week', optionLabel: 'display', showAvailable: false }"
            :loading="endWeeksOfYearLoading"
            :options="endWeeksOfYear"
            :originalValue="endWeekOfYearOriginalValue"
            @onSelected="onSelectEndWeekOfYearFilter"
            @onClear="onClearEndWeekOfYearFilter"
          />
        </div>
        <div class="col-span-12 ml-1">
          <RMSAutoComplete
            ref="skuFilterRef"
            :fieldId="'sku-autocomplete'"
            :config="{ label: 'SKU', showAvailable: false }"
            :loading="skusLoading"
            :options="skus"
            @onSelected="onSelectSkuFilter"
            @onClear="onClearSkuFilter"
          />
        </div>
      </div>
      <div v-if="inventoryAggregateLoading" class="m-16">
        loading...
      </div>
      <div v-else>
        <div class="grid grid-cols-12 mb-3">
          <strong class="text-red-600 col-span-6 text-right mr-2">
            {{ inventoryAggregate.inventory_start?.toLocaleString('en-US') }}
          </strong>
          <span class="col-span-6">Start Inventory</span>
        </div>
        <div class="grid grid-cols-12 mb-3">
          <strong class="text-red-600 col-span-6 text-right mr-2">
            {{ inventoryAggregate.inventory_end?.toLocaleString('en-US') }}
          </strong>
          <span class="col-span-6">End Inventory</span>
        </div>
        <div class="grid grid-cols-12 mb-3">
          <strong class="text-red-600 col-span-6 text-right mr-2">
            {{ inventoryAggregate.notifications?.toLocaleString('en-US') }}
          </strong>
          <span class="col-span-6">Notifications</span>
        </div>
        <div class="grid grid-cols-12 mb-3">
          <strong class="text-red-600 col-span-6 text-right mr-2">
            {{ inventoryAggregate.turns?.toFixed(4) }}
          </strong>
          <span class="col-span-6">Annualized Turns</span>
        </div>
      </div>
    </template>
  </Card>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import Card from 'primevue/card'
import RMSAutoComplete from '@/components/fields/RMSAutoComplete.vue'
import ApiUtility from '@/utilities/ApiUtility'
import WeeksOfYearService from '@/services/WeeksOfYearService'
import AnnualizedTurnDetailsService from '@/services/activity/annualizedTurns/AnnualizedTurnDetailsService'
import SkusService from '@/services/skus/SkusService'
import formattingUtilities from '@/utilities/formattingUtilities'
import { DateTime } from 'luxon'

export default {
  components: {
    RMSAutoComplete,
    Card
  },
  props: {
    program: {
      type: Object,
      default: () => {}
    }
  },
  setup(props) {

    const weekOfYearIdBetweenIsValid = computed(() => {
      return !!weekOfYearIdBetween.value[0] && !!weekOfYearIdBetween.value[1]
    })

    const inventoryAggregateApi = new ApiUtility()
    const inventoryAggregate = ref({})
    const inventoryAggregateLoading = ref(true)
    const skusApi = new ApiUtility()
    const skuFilterRef = ref()
    const skus = ref([])
    const skusLoading = ref(true)
    const startWeeksOfYearFilterRef = ref()
    const startWeeksOfYearApi = new ApiUtility()
    const startWeekOfYearOriginalValue = ref({})
    const startWeeksOfYear = ref([])
    const startWeeksOfYearLoading = ref(true)
    const endWeeksOfYearFilterRef = ref()
    const endWeeksOfYearApi = new ApiUtility()
    const endWeekOfYearOriginalValue = ref({})
    const endWeeksOfYear = ref([])
    const endWeeksOfYearLoading = ref(true)
    const weekOfYearIdBetween = ref([])

    onMounted(() => {
      startWeeksOfYearApi.init(new WeeksOfYearService(), {
        start_less_than_date: DateTime.fromJSDate(new Date()).minus({ week: 1 }).toSQLDate(),
      })
      startWeeksOfYearApi.setSelect(['id', 'week', 'year'].join())
      startWeeksOfYearApi.setParams({ sort_by: 'id', sort_dir: 'desc' })
      startWeeksOfYearApi.setGetAllEntries()
      startWeeksOfYearApi.loadData(handleStartWeeksOfYearApiResponse)

      endWeeksOfYearApi.init(new WeeksOfYearService(), {
        start_less_than_date: DateTime.fromJSDate(new Date()).minus({ week: 1 }).toSQLDate(),
      })
      endWeeksOfYearApi.setSelect(['id', 'week', 'year'].join())
      endWeeksOfYearApi.setParams({ sort_by: 'id', sort_dir: 'desc' })
      endWeeksOfYearApi.setGetAllEntries()
      endWeeksOfYearApi.loadData(handleEndWeeksOfYearApiResponse)

      skusApi.init(new SkusService(), {
        program_id: props.program.id,
      })
      skusApi.setSelect(['id', 'name', 'type'].join())
      skusApi.setParams({ sort_by: 'name', sort_dir: 'asc' })
      skusApi.setWith('components')
      skusApi.setGetAllEntries()
      skusApi.loadData(handleSkusApiResponse)

      inventoryAggregateApi.init(new AnnualizedTurnDetailsService(), {
        program_id: props.program.id,
      })
      inventoryAggregateApi.setGetAllEntries()
    })

    const handleInventoryAggregateApiResponse = (response) => {
      inventoryAggregate.value = response
      inventoryAggregateLoading.value = false
    }

    const handleStartWeeksOfYearApiResponse = (response) => {
      startWeeksOfYear.value = response.data.map((woy) => {
        woy.display = formattingUtilities.concatenateWeekOfYear(woy)
        return woy
      })
      startWeekOfYearOriginalValue.value = startWeeksOfYear.value[9]
      startWeeksOfYearLoading.value = false
    }

    const handleEndWeeksOfYearApiResponse = (response) => {
      endWeeksOfYear.value = response.data.map((woy) => {
        woy.display = formattingUtilities.concatenateWeekOfYear(woy)
        return woy
      })
      endWeekOfYearOriginalValue.value = endWeeksOfYear.value[0]
      endWeeksOfYearLoading.value = false
    }

    const handleSkusApiResponse = (response) => {
      skus.value = response.data
      skusLoading.value = false
    }

    watch(
      () => props.program,
      () => {
        skuFilterRef.value.resetSelected()
        skusApi.clearFilterNoLoad('program_id')
        skusApi.addFilterNoLoad('program_id', props.program.id)
        skusApi.loadData(handleSkusApiResponse)
        inventoryAggregateApi.clearFilterNoLoad(['sku_id', 'sku_id_in', 'program_id'])
        inventoryAggregateApi.addFilterNoLoad('program_id', props.program.id)
        loadData()
      }
    )

    const loadData = () => {
      inventoryAggregateLoading.value = true
      inventoryAggregateApi.loadData(handleInventoryAggregateApiResponse)
    }

    const onSelectSkuFilter = (sku) => {
      switch (sku.type) {
        case 'component':
          inventoryAggregateApi.addFilterNoLoad('sku_id', sku.id)
          inventoryAggregateApi.clearFilterNoLoad('sku_id_in')
          break
        case 'unit':
          inventoryAggregateApi.addFilterNoLoad('sku_id_in', sku.components.map(c => c.component_id))
          inventoryAggregateApi.clearFilterNoLoad('sku_id')
          break
      }
      loadData()
    }
    const onClearSkuFilter = () => {
      inventoryAggregateApi.clearFilterNoLoad(['sku_id', 'sku_id_in'])
      loadData()
    }

    const onSelectStartWeekOfYearFilter = (woy) => {
      weekOfYearIdBetween.value[0] = woy.id
      if (weekOfYearIdBetweenIsValid.value) {
        inventoryAggregateApi.addFilterNoLoad('week_of_year_id_between', weekOfYearIdBetween.value)
        loadData()
      }
    }
    const onClearStartWeekOfYearFilter = () => {
      weekOfYearIdBetween.value[0] = undefined
      inventoryAggregateApi.clearFilterNoLoad('week_of_year_id_between')
      loadData()
    }

    const onSelectEndWeekOfYearFilter = (woy) => {
      weekOfYearIdBetween.value[1] = woy.id
      if (weekOfYearIdBetweenIsValid.value) {
        inventoryAggregateApi.addFilterNoLoad('week_of_year_id_between', weekOfYearIdBetween.value)
        loadData()
      }
    }
    const onClearEndWeekOfYearFilter = () => {
      weekOfYearIdBetween.value[1] = undefined
      inventoryAggregateApi.clearFilterNoLoad('week_of_year_id_between')
      loadData()
    }

    return {
      inventoryAggregate,
      inventoryAggregateLoading,
      startWeeksOfYearFilterRef,
      startWeeksOfYear,
      startWeeksOfYearLoading,
      startWeekOfYearOriginalValue,
      onSelectStartWeekOfYearFilter,
      onClearStartWeekOfYearFilter,
      endWeeksOfYearFilterRef,
      endWeeksOfYear,
      endWeeksOfYearLoading,
      endWeekOfYearOriginalValue,
      onSelectEndWeekOfYearFilter,
      onClearEndWeekOfYearFilter,
      skuFilterRef,
      skus,
      skusLoading,
      onSelectSkuFilter,
      onClearSkuFilter,
    }
  },
}
</script>
